import React, { useState } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";

import {
  faPhone,
  faEnvelope,
  faLocationDot,
  faClock,
  // faPaperPlane,
  faArrowRight,
  faCircleQuestion,
  faCheck,
  faLifeRing,
  faScrewdriverWrench,
  faFileInvoice,
} from "@fortawesome/free-solid-svg-icons";
import "./Contact.css";
import { useLocalContext } from "../../context/LocalContext";
import { useMessageContext } from "../../context/MessageContext";
import { toast } from "react-toastify";

const fx = (d = 0) => ({
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.5, delay: d, ease: "easeOut" },
  viewport: { once: true, amount: 0.2 },
});

export default function Contact() {
  const { webinfo = {}, openWhatsApp } = useLocalContext();
  const { addMessage } = useMessageContext();

  const phone = webinfo.phone || "";
  const phoneHref = webinfo.phonecall || phone || "";
  const phoneSecondary = webinfo.phoneSecondary || "";
  const phoneSecondaryHref = webinfo.phoneSecondaryCall || phoneSecondary || "";
  const email = webinfo.email || "";
  const address = webinfo.address || "Remote • India based";
  const hours = webinfo.hours || "Mon-Sat • 9am to 9pm";
  const phoneItems = [
    phone ? { label: phone, href: phoneHref } : null,
    phoneSecondary
      ? { label: phoneSecondary, href: phoneSecondaryHref }
      : null,
  ].filter(Boolean);

  const [sending, setSending] = useState(false);
  const [faqOpen, setFaqOpen] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    website: "", 
  });

  const onChange = (e) =>
    setForm((f) => ({
      ...f,
      [e.target.name]: e.target.value,
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.website) return; 

    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill out Name, Email, and Message.");
      return;
    }

    try {
      setSending(true);
      await addMessage({
        type: "contact",
        text: form.message.trim(),
        author: "user",
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        source: "contact-v4",
        meta: { subject: form.subject.trim() || null },
      });
      toast.success("Thanks! We’ll reply within a day.");
      openWhatsApp?.({
        message: `Hi! My name is ${form.name.trim()}. ${
          form.subject.trim()
            ? `Subject: ${form.subject.trim()}. `
            : ""
        }${form.message.trim()}${
          form.phone.trim() ? ` My phone number is ${form.phone.trim()}.` : ""
        }`,
      });
      setForm({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
        website: "",
      });
      window?.dataLayer?.push({
        event: "contact_submit",
        origin: "contact-v4",
      });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const FaqItem = ({ i, q, a }) => {
    const open = faqOpen === i;
    return (
      <div className={`cv4-faq-item ${open ? "open" : ""}`}>
        <button
          className="cv4-faq-q"
          onClick={() => setFaqOpen(open ? null : i)}
          aria-expanded={open}
        >
          <FontAwesomeIcon icon={faCircleQuestion} />
          <span>{q}</span>
          <span className="cv4-faq-caret" aria-hidden="true">
            ▾
          </span>
        </button>
        <div className="cv4-faq-a">
          <p>{a}</p>
        </div>
      </div>
    );
  };

  return (
    <section className="cv4-wrap" role="main">
      {/* HERO */}
      <header className="cv4-hero">
        <motion.div className="cv4-hero-copy" {...fx(0)}>
          <span className="cv4-pill">Let’s work together</span>
          <h1>Tell us what you’re building</h1>
          <p className="cv4-lead">
            Share a quick brief and we’ll respond with scope options, timelines,
            and the next right step—no fluff, just practical guidance.
          </p>
          <ul className="cv4-points" role="list">
            <li>
              <FontAwesomeIcon icon={faCheck} /> 24-hour response
            </li>
            <li>
              <FontAwesomeIcon icon={faCheck} /> Transparent pricing ranges
            </li>
            <li>
              <FontAwesomeIcon icon={faCheck} /> Friendly technical guidance
            </li>
          </ul>
        </motion.div>

        <motion.aside className="cv4-hero-rail" {...fx(0.05)}>
          <h3>Quick contact</h3>
          <ul className="cv4-contacts" role="list">
            <li>
              <span className="cv4-label">
                <FontAwesomeIcon icon={faPhone} /> Phone
              </span>
              <span className="cv4-multi-contact">
                {phoneItems.length ? (
                  phoneItems.map((item) => (
                    <a
                      key={item.href}
                      href={item.href ? `tel:${item.href}` : "#"}
                      aria-disabled={!item.href}
                    >
                      {item.label}
                    </a>
                  ))
                ) : (
                  <a href="#" aria-disabled="true">—</a>
                )}
              </span>
            </li>
            <li>
              <span className="cv4-label">
                <FontAwesomeIcon icon={faEnvelope} /> Email
              </span>
              <a href={email ? `mailto:${email}` : "#"} aria-disabled={!email}>
                {email || "—"}
              </a>
            </li>
            <li>
              <span className="cv4-label">
                <FontAwesomeIcon icon={faClock} /> Hours
              </span>
              <span>{hours}</span>
            </li>
            <li>
              <span className="cv4-label">
                <FontAwesomeIcon icon={faLocationDot} /> Location
              </span>
              <span>{address}</span>
            </li>
          </ul>
          <a
            className="cv4-btn ghost full"
            href={email ? `mailto:${email}` : "#"}
            aria-disabled={!email}
          >
            Write an email <FontAwesomeIcon icon={faArrowRight} />
          </a>
        </motion.aside>
      </header>

      {/* SUPPORT STRIP */}
      <motion.section className="cv4-support" {...fx(0.1)}>
        <article>
          <span className="cv4-ico">
            <FontAwesomeIcon icon={faLifeRing} />
          </span>
          <h4>Sales</h4>
          <p>Ideas, quotes, timelines, and scope questions.</p>
        </article>
        <article>
          <span className="cv4-ico">
            <FontAwesomeIcon icon={faScrewdriverWrench} />
          </span>
          <h4>Tech</h4>
          <p>Integrations, APIs, performance, deployment.</p>
        </article>
        <article>
          <span className="cv4-ico">
            <FontAwesomeIcon icon={faFileInvoice} />
          </span>
          <h4>Billing</h4>
          <p>Invoices, renewals, vendor forms, NDAs.</p>
        </article>
      </motion.section>

      <div className="cv4-grid">
        <motion.section className="cv4-card" {...fx(0.12)}>
          <form onSubmit={onSubmit} noValidate>
            <input
              className="cv4-hp"
              type="text"
              name="website"
              value={form.website}
              onChange={onChange}
              tabIndex="-1"
              autoComplete="off"
              aria-hidden="true"
            />

            <div className="cv4-row">
              <div className="cv4-field">
                <label htmlFor="name">Your Name</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Jane Doe"
                  autoComplete="name"
                  inputMode="text"
                  value={form.name}
                  onChange={onChange}
                  required
                />
              </div>
              <div className="cv4-field">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="jane@company.com"
                  autoComplete="email"
                  inputMode="email"
                  value={form.email}
                  onChange={onChange}
                  required
                />
              </div>
            </div>

            <div className="cv4-row">
              <div className="cv4-field">
                <label htmlFor="phone">Phone (optional)</label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+91 9*********"
                  autoComplete="tel"
                  inputMode="tel"
                  value={form.phone}
                  onChange={onChange}
                />
              </div>
              <div className="cv4-field">
                <label htmlFor="subject">Subject</label>
                <input
                  id="subject"
                  name="subject"
                  type="text"
                  placeholder="Project, question, or idea"
                  value={form.subject}
                  onChange={onChange}
                />
              </div>
            </div>

            <div className="cv4-field">
              <label htmlFor="message">Your Message</label>
              <textarea
                id="message"
                name="message"
                rows="7"
                placeholder="Tell us a bit about what you need…"
                value={form.message}
                onChange={onChange}
                required
              />
            </div>

            <div className="cv4-actions">
              <button
                className="cv4-btn solid"
                type="submit"
                disabled={sending}
                aria-busy={sending}
              >
                <FontAwesomeIcon icon={faWhatsapp} />{" "}
                {sending ? "Sending…" : "Send message"}
              </button>
              <span className="cv4-note">
                We usually reply within one business day.
              </span>
            </div>
          </form>

          <div className="cv4-faq">
  <h3>Questions people ask</h3>

  <FaqItem
    i={0}
    q="Can you share an estimate before a full brief?"
    a="Yes. Share your goals and basic requirements, and we'll provide a rough estimate. After understanding your brand in detail, we’ll share a complete proposal with timeline and deliverables."
  />

  <FaqItem
    i={1}
    q="Do you provide both branding and digital marketing services?"
    a="Yes. We offer end-to-end solutions including branding, graphic design, content creation, social media management, ad campaigns, and growth strategy."
  />

  <FaqItem
    i={2}
    q="How soon can we start?"
    a="We usually begin within 24–72 hours after onboarding. Our team quickly aligns on strategy and starts execution."
  />

</div>
        </motion.section>

        <motion.aside className="cv4-card cv4-rail" {...fx(0.16)}>
          
          <div className="cv4-rail-group">
            <h4>
              <FontAwesomeIcon icon={faPhone} /> Phone
            </h4>
            <div className="cv4-rail-link cv4-multi-contact" aria-disabled={!phoneItems.length}>
              {phoneItems.length ? (
                phoneItems.map((item) => (
                  <a
                    key={item.href}
                    href={item.href ? `tel:${item.href}` : "#"}
                    aria-disabled={!item.href}
                  >
                    {item.label}
                  </a>
                ))
              ) : (
                <span>—</span>
              )}
            </div>
          </div>

          <div className="cv4-rail-group">
            <h4>
              <FontAwesomeIcon icon={faEnvelope} /> Email
            </h4>
            <a
              className="cv4-rail-link"
              href={email ? `mailto:${email}` : "#"}
              aria-disabled={!email}
            >
              {email || "—"}
            </a>
          </div>

          <div className="cv4-rail-group">
            <h4>
              <FontAwesomeIcon icon={faLocationDot} /> Location
            </h4>
            <p className="cv4-rail-text">{address}</p>
          </div>

          <div className="cv4-rail-group">
            <h4>
              <FontAwesomeIcon icon={faClock} /> Hours
            </h4>
            <p className="cv4-rail-text">{hours}</p>
          </div>

          <div className="cv4-rail-cta">
            <a
              className="cv4-btn ghost"
              href={email ? `mailto:${email}` : "#"}
              aria-disabled={!email}
            >
              Write an email <FontAwesomeIcon icon={faArrowRight} />
            </a>
          </div>
        </motion.aside>
      </div>

      
      <motion.div className="cv4-map" {...fx(0.2)}>
        <iframe
          title="Branding Studios Location"
          src="https://maps.google.com/maps?q=Indradhanu+Market+Nayapalli+Bhubaneswar&t=&z=13&ie=UTF8&iwloc=&output=embed"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        />
      </motion.div>
    </section>
  );
}
